import { apiFetch, apiUrl, OFFLINE_MESSAGE } from "./api";
import { ApiError } from "./incident-api";
import { maybeUpsellPlanLimit, PlanLimitError } from "./plan-limit-store";

export { ApiError };

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await apiFetch(apiUrl(path), {
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
      ...init,
    });
  } catch (err) {
    if (err instanceof TypeError && /fetch/i.test(err.message)) {
      throw new ApiError(OFFLINE_MESSAGE, 0);
    }
    throw err;
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Request failed (${res.status})`;
    if (maybeUpsellPlanLimit(res.status, data)) throw new PlanLimitError(message);
    throw new ApiError(message, res.status);
  }
  return data as T;
}

export type Audience =
  | { kind: "all" }
  | { kind: "department"; values: string[] }
  | { kind: "status"; values: string[] }
  | { kind: "selection"; values: string[] }
  | { kind: "list"; values: string[] };

/**
 * Placeholder tokens HR can drop into a subject/body. Resolved per-recipient
 * (employee tokens) or globally (org/sender) at send time. Mirrors the backend
 * catalog in `lib/broadcast-template.ts`.
 */
export type PlaceholderToken = {
  token: string;
  label: string;
  example: string;
};

export const EMPLOYEE_PLACEHOLDERS: PlaceholderToken[] = [
  { token: "first_name", label: "First name", example: "Jane" },
  { token: "last_name", label: "Last name", example: "Doe" },
  { token: "full_name", label: "Full name", example: "Jane Doe" },
  { token: "preferred_name", label: "Preferred name", example: "Jane" },
  { token: "title", label: "Title", example: "Ms." },
  { token: "email", label: "Email", example: "jane@acme.com" },
  { token: "department", label: "Department", example: "Engineering" },
  { token: "job_title", label: "Job title", example: "Software Engineer" },
  {
    token: "employee_number",
    label: "Employee number",
    example: "EMP-001",
  },
];

export const GLOBAL_PLACEHOLDERS: PlaceholderToken[] = [
  { token: "company_name", label: "Company name", example: "Acme Inc." },
  { token: "sender_name", label: "Sender name", example: "You" },
  { token: "today", label: "Today's date", example: "June 21, 2026" },
];

export const ALL_PLACEHOLDERS = [...EMPLOYEE_PLACEHOLDERS, ...GLOBAL_PLACEHOLDERS];

/** Replace `{{token}}` with each token's example value (for preview). */
export function renderPreview(input: string): string {
  const map = new Map(ALL_PLACEHOLDERS.map((p) => [p.token, p.example]));
  return input.replace(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi, (match, name: string) => {
    const value = map.get(name.toLowerCase());
    return value ?? match;
  });
}

export type Broadcast = {
  id: string;
  subject: string;
  body: string;
  audience: Audience;
  channel: string;
  status: "draft" | "sending" | "sent" | "failed";
  recipientCount: number;
  sentCount: number;
  failedCount: number;
  sentBy: string | null;
  sentAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export type BroadcastRecipient = {
  id: string;
  employeeId: string | null;
  email: string;
  name: string | null;
  deliveryStatus: "queued" | "sent" | "failed";
  error: string | null;
  sentAt: string | null;
};

export type BroadcastInput = {
  subject: string;
  body: string;
  audience: Audience;
  sendNow: boolean;
};

export type BroadcastList = {
  id: string;
  name: string;
  description: string | null;
  memberCount: number;
  createdAt: string;
  updatedAt: string;
};

export type BroadcastListInput = {
  name: string;
  description?: string | null;
  employeeIds: string[];
};

export async function listBroadcasts(): Promise<Broadcast[]> {
  const data = await request<{ items: Broadcast[] }>("/api/broadcasts");
  return data.items;
}

/** Filters + paging for the broadcast history (server-side). */
export type BroadcastsPageParams = {
  limit: number;
  offset: number;
  q?: string;
  status?: Broadcast["status"];
  sort?: "subject" | "delivery" | "created";
  dir?: "asc" | "desc";
};

/** One page of broadcasts plus the overall total for the active filters. */
export type BroadcastsPage = { items: Broadcast[]; total: number };

/** A filtered, paginated slice of the broadcast history (`/api/broadcasts/page`). */
export async function fetchBroadcastsPage(params: BroadcastsPageParams): Promise<BroadcastsPage> {
  const search = new URLSearchParams({
    limit: String(params.limit),
    offset: String(params.offset),
  });
  if (params.q) search.set("q", params.q);
  if (params.status) search.set("status", params.status);
  if (params.sort) search.set("sort", params.sort);
  if (params.dir) search.set("dir", params.dir);
  return request<BroadcastsPage>(`/api/broadcasts/page?${search.toString()}`);
}

export async function getBroadcast(
  id: string,
): Promise<{ item: Broadcast; recipients: BroadcastRecipient[] }> {
  return request<{ item: Broadcast; recipients: BroadcastRecipient[] }>(`/api/broadcasts/${id}`);
}

export async function createBroadcast(
  input: BroadcastInput,
): Promise<{ item: Broadcast; queued: boolean }> {
  return request<{ item: Broadcast; queued: boolean }>("/api/broadcasts", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function sendDraft(id: string): Promise<{ item: Broadcast; queued: boolean }> {
  return request<{ item: Broadcast; queued: boolean }>(`/api/broadcasts/${id}/send`, {
    method: "POST",
  });
}

// ── Distribution lists ──────────────────────────────────────────────────────

export async function listBroadcastLists(): Promise<BroadcastList[]> {
  const data = await request<{ items: BroadcastList[] }>("/api/broadcasts/lists/all");
  return data.items;
}

export async function getBroadcastList(
  id: string,
): Promise<{ item: BroadcastList; employeeIds: string[] }> {
  return request<{ item: BroadcastList; employeeIds: string[] }>(`/api/broadcasts/lists/${id}`);
}

export async function createBroadcastList(
  input: BroadcastListInput,
): Promise<{ item: BroadcastList }> {
  return request<{ item: BroadcastList }>("/api/broadcasts/lists", {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function updateBroadcastList(
  id: string,
  input: Partial<BroadcastListInput>,
): Promise<{ item: BroadcastList }> {
  return request<{ item: BroadcastList }>(`/api/broadcasts/lists/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
}

export async function deleteBroadcastList(id: string): Promise<void> {
  await request<void>(`/api/broadcasts/lists/${id}`, { method: "DELETE" });
}
